import React, { useState } from 'react';
import { Upload, X } from 'lucide-react';

const AddWidgetModal = ({
  newWidget,
  handleInputChange,
  handleSaveWidget,
  onClose,
  tools,
  tabs,
  existingWidgets = [],
  widgetContents = {}
}) => {
  const [selectedContextWidgets, setSelectedContextWidgets] = useState([]);
  const [ragFile, setRagFile] = useState(null);

  // Handle widget selection for context
  const handleWidgetContextSelection = (widgetId) => {
    setSelectedContextWidgets(prev => {
      if (prev.includes(widgetId)) {
        return prev.filter(id => id !== widgetId);
      } else {
        return [...prev, widgetId];
      }
    });
  };

  // Combine the selected widget contents when saving
  const handleSaveWithContext = () => {
    const combinedContext = selectedContextWidgets
      .map(widgetId => {
        const widget = existingWidgets.find(w => w.id === widgetId);
        const content = widgetContents[widgetId] || '';
        return `[${widget?.title || 'Widget'}]: ${content}`;
      })
      .join('\n\n');
    
    handleSaveWidget(combinedContext, newWidget.tool === 'Retrieval Tool' ? ragFile : null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[1000] backdrop-blur-sm">
      <div className="bg-white rounded-lg p-6 w-[550px] max-w-[90%] shadow-xl border border-gray-200 animate-fadeInUp max-h-[90vh] overflow-y-auto">
        <h2 className="mt-0 mb-5 text-xl font-semibold border-b border-gray-100 pb-3">
          Create New Widget
        </h2>
        
        <div className="mb-4">
          <label htmlFor="title" className="block mb-1.5 font-medium text-sm">
            Widget Title:
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={newWidget.title}
            onChange={handleInputChange}
            placeholder="Enter widget title"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-all bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
            required
          />
        </div>
        
        <div className="mb-4">
          <label className="block mb-1.5 font-medium text-sm">Widget Type:</label>
          <div className="flex gap-5 py-2">
            <label className="flex items-center text-sm font-normal cursor-pointer">
              <input
                type="radio"
                name="widgetType"
                value="text"
                checked={newWidget.widgetType === 'text'}
                onChange={handleInputChange}
                className="mr-1.5 accent-secondary cursor-pointer"
              />
              Text Widget
            </label>
            <label className="flex items-center text-sm font-normal cursor-pointer">
              <input
                type="radio"
                name="widgetType"
                value="visualization"
                checked={newWidget.widgetType === 'visualization'}
                onChange={handleInputChange}
                className="mr-1.5 accent-secondary cursor-pointer"
              />
              Visualization Widget
            </label>
          </div>
        </div>
        
        <div className="mb-4">
          <label htmlFor="tool" className="block mb-1.5 font-medium text-sm">
            Tool:
          </label>
          <select
            id="tool"
            name="tool"
            value={newWidget.tool}
            onChange={handleInputChange}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-all bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
          >
            {tools.map(tool => (
              <option key={tool.id} value={tool.name}>
                {tool.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="mb-4">
          <label htmlFor="prompt" className="block mb-1.5 font-medium text-sm">
            Prompt:
          </label>
          <textarea
            id="prompt"
            name="prompt"
            value={newWidget.prompt}
            onChange={handleInputChange}
            placeholder="Enter the prompt for this widget"
            rows="3"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-all bg-gray-50 resize-y min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
            required
          />
        </div>
        
        {newWidget.widgetType === 'visualization' && (
          <div className="mb-4">
            <label htmlFor="visualPrompt" className="block mb-1.5 font-medium text-sm">
              Visualization Prompt:
            </label>
            <textarea
              id="visualPrompt"
              name="visualPrompt"
              value={newWidget.visualPrompt}
              onChange={handleInputChange}
              placeholder="Enter the visualization prompt"
              rows="3"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-all bg-gray-50 resize-y min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
              required
            />
          </div>
        )}
        
        {newWidget.tool === 'Retrieval Tool' && (
          <div className="mb-4">
            <label className="block mb-1.5 font-medium text-sm">
              Upload Document:
            </label>
            <div className="border-2 border-dashed border-gray-200 rounded-md bg-gray-50">
              <input
                type="file"
                id="ragFileInput"
                accept=".pdf,.txt,.docx,.doc,.csv"
                className="hidden"
                onChange={(e) => setRagFile(e.target.files[0] || null)}
              />
              {ragFile ? (
                <div className="flex items-center justify-between px-3 py-2.5">
                  <div className="flex items-center gap-2 text-sm text-green-700 font-medium truncate">
                    <Upload size={14} className="shrink-0" />
                    <span className="truncate">{ragFile.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setRagFile(null)}
                    className="ml-2 text-gray-400 hover:text-gray-600 shrink-0"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="ragFileInput"
                  className="flex flex-col items-center gap-1.5 py-4 cursor-pointer"
                >
                  <Upload size={18} className="text-gray-400" />
                  <span className="text-sm text-gray-500">Click to upload document</span>
                  <span className="text-xs text-gray-400">PDF, TXT, DOCX, CSV</span>
                </label>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Document will be uploaded to this widget's retrieval collection
            </p>
          </div>
        )}

        {/* New section for selecting context widgets */}
        <div className="mb-4">
          <label className="block mb-1.5 font-medium text-sm">
            Use Content from Existing Widgets as Context:
          </label>
          <div className="border border-gray-200 rounded-md p-2 max-h-[200px] overflow-y-auto bg-gray-50">
            {existingWidgets.length > 0 ? (
              existingWidgets.map(widget => (
                <div key={widget.id} className="flex items-center py-1.5 border-b border-gray-100 last:border-b-0">
                  <input
                    type="checkbox"
                    id={`widget-context-${widget.id}`}
                    checked={selectedContextWidgets.includes(widget.id)}
                    onChange={() => handleWidgetContextSelection(widget.id)}
                    className="w-4 h-4 accent-secondary cursor-pointer mr-2"
                  />
                  <label 
                    htmlFor={`widget-context-${widget.id}`} 
                    className="text-sm cursor-pointer flex-1 truncate"
                  >
                    {widget.title || `Widget ${widget.id}`}
                  </label>
                  
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 py-2 text-center">No existing widgets available</p>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Selected widget content will be used as context for this new widget
          </p>
        </div>
        
        <div className="mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="hasAvatar"
              checked={newWidget.hasAvatar}
              onChange={handleInputChange}
              className="w-4 h-4 accent-secondary cursor-pointer"
            />
            <span className="text-sm font-medium">Enable Avatar for this widget</span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            When enabled, content will be presented with an interactive avatar
          </p>
        </div>
        
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button
            className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md font-medium text-sm transition-all hover:bg-gray-200"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className={`px-4 py-2 bg-secondary text-white rounded-md font-medium text-sm transition-all ${
              !newWidget.title || !newWidget.prompt || (newWidget.widgetType === 'visualization' && !newWidget.visualPrompt)
                ? 'bg-secondary-light cursor-not-allowed'
                : 'hover:bg-secondary-dark'
            }`}
            onClick={handleSaveWithContext}
            disabled={!newWidget.title || !newWidget.prompt || (newWidget.widgetType === 'visualization' && !newWidget.visualPrompt)}
          >
            Save Widget
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddWidgetModal;